import { QualityLevel } from "../types";
import { currentTimeCacheKey } from "../utils";
import { enterFullScreen, exitFullScreen } from "../utils/fullscreen";
import { isIOS } from "../utils/device-detection";
import { useVideoPlayer2SetupStart } from "./use-video-player-2-setup-start";
import { useVideoPlayer2HlsSetup } from "./useVideoPlayer2HlsSetup";
import { useVideoPlayer2Init } from "./useVideoPlayer2Init";

export function useVideoPlayer2Setup() {
  useVideoPlayer2VolumeSetup();
  useVideoPlayer2TimeSetup();
  useVideoPlayer2FullscreenSetup();
  useVideoPlayer2PictureInPictureSetup();
  useVideoPlayer2PlaybackSetup();
  useVideoPlayer2HlsSetup();
  useVideoPlayer2Init();
  useVideoPlayer2KeyboardShortcutsSetup();
  useVideoPlayer2QualitiesSetup();
  useVideoPlayer2VideoTouchSetup();
  useVideoPlayer2LoadingSetup();
  useVideoPlayer2EventsSetup();
  useVideoPlayer2ControlsAutoHide();
}

function useVideoPlayer2EventsSetup() {
  useVideoPlayer2SetupStart(({ player, video }) => {
    let hasStarted = false;
    let lastSeekTime = 0;

    const onStart = () => {
      if (!hasStarted) {
        hasStarted = true;
        player.triggerEvent("started", player);
        player.props?.onStart?.(player, video.currentTime);
      }
    };

    const onEnded = () => {
      player.triggerEvent("ended", player);
      player.props?.onEnded?.(player, video.duration);

      window.localStorage.removeItem(currentTimeCacheKey(player));
    };

    const onPlay = () => {
      player.triggerEvent("play", player);
      player.props?.onPlay?.(player);
    };

    const onPause = () => {
      player.triggerEvent("pause", player);
      player.props?.onPause?.(player);
    };

    const onSeeking = () => {
      const fromTime = lastSeekTime;
      const toTime = video.currentTime;
      lastSeekTime = toTime;
      
      player.triggerEvent("seeking", player);
      player.props?.onSeeking?.(player, fromTime, toTime);
    };

    const onSeeked = () => {
      const toTime = video.currentTime;
      
      player.triggerEvent("seeked", player);
      player.props?.onSeeked?.(player, toTime);
    };

    video.addEventListener("play", onStart);
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onEnded);
    video.addEventListener("seeking", onSeeking);
    video.addEventListener("seeked", onSeeked);

    return () => {
      video.removeEventListener("play", onStart);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("seeking", onSeeking);
      video.removeEventListener("seeked", onSeeked);
    };
  });
}

function useVideoPlayer2VolumeSetup() {
  useVideoPlayer2SetupStart(({ player, video }) => {
    player.setVolume = (volume: number) => {
      // HTMLMediaElement.volume throws IndexSizeError outside [0, 1]
      // (keyboard/wheel steps can overshoot, float math can go slightly < 0).
      video.volume = Number.isFinite(volume)
        ? Math.min(1, Math.max(0, volume))
        : video.volume;

      player.triggerEvent("volumeChanged", player);
    };

    player.increaseVolume = (value: number) => {
      player.setVolume(player.volume + value);
    };

    player.decreaseVolume = (value: number) => {
      player.setVolume(player.volume - value);
    };

    player.$lazyGetter("volume", () => video?.volume || 0);

    player.$lazyGetter("isMuted", () => video?.muted || false);

    player.toggleMute = () => {
      video.muted = !video.muted;
      player.triggerEvent("mutedChanged", player);
    };

    player.setMute = (mute: boolean) => {
      video.muted = mute;
      player.triggerEvent("mutedChanged", player);
    };
  });
}

function useVideoPlayer2TimeSetup() {
  useVideoPlayer2SetupStart(({ player, video }) => {
    player.setCurrentTime = (time: number) => {
      const fromTime = video.currentTime;
      video.currentTime = time;
      player.triggerEvent("currentTimeChanged", player);
      
      // Trigger seeking events for progress bar interactions
      // Use setTimeout to ensure the video time has actually changed
      if (Math.abs(fromTime - time) > 0.1) { // Only if significant time change
        setTimeout(() => {
          player.props?.onSeeking?.(player, fromTime, video.currentTime);
          player.props?.onSeeked?.(player, video.currentTime);
        }, 10);
      }
    };

    player.$lazyGetter("currentTime", () => video?.currentTime || 0);
  });
}

function useVideoPlayer2FullscreenSetup() {
  useVideoPlayer2SetupStart(({ player, video }) => {


    player.exitFullscreen = () => {
      exitFullScreen();
      player.triggerEvent("fullscreenChanged", player);
    };

    player.enterFullscreen = () => {
      enterFullScreen(player.rootRef.current!);
      player.triggerEvent("fullscreenChanged", player);
    };

    player.toggleFullscreen = () => {
      // For iOS, check if video is in fullscreen mode
      if (isIOS()) {
        const isVideoFullscreen = (video as any).webkitDisplayingFullscreen;
        if (isVideoFullscreen) {
          exitFullScreen();
        } else {
          enterFullScreen(player.rootRef.current!);
        }
      } else {
        // Standard fullscreen check
        document.fullscreenElement
          ? exitFullScreen()
          : enterFullScreen(player.rootRef.current!);
      }

      player.triggerEvent("fullscreenChanged", player);
    };

    // Update the isFullscreen getter to handle iOS
    player.$lazyGetter("isFullscreen", () => {
      if (isIOS()) {
        return (video as any).webkitDisplayingFullscreen || false;
      }
      return document.fullscreenElement === player.element;
    });

    // Add fullscreen change event listeners
    const handleFullscreenChange = () => {
      player.triggerEvent("fullscreenChanged", player);
    };

    // Standard fullscreen events
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    // iOS-specific fullscreen events
    if (isIOS()) {
      video.addEventListener('webkitbeginfullscreen', handleFullscreenChange);
      video.addEventListener('webkitendfullscreen', handleFullscreenChange);
    }

    // Cleanup function
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      
      if (isIOS()) {
        video.removeEventListener('webkitbeginfullscreen', handleFullscreenChange);
        video.removeEventListener('webkitendfullscreen', handleFullscreenChange);
      }
    };
  });
}

function useVideoPlayer2PictureInPictureSetup() {
  useVideoPlayer2SetupStart(({ player }) => {
    player.togglePictureInPicture = () => {
      if (document.pictureInPictureElement) {
        document.exitPictureInPicture().catch(() => undefined);
        player.isPictureInPicture = false;
      } else {
        const element = player.element;

        if (
          !element ||
          !document.pictureInPictureEnabled ||
          typeof element.requestPictureInPicture !== "function"
        ) {
          return;
        }

        // Rejects when PiP is unavailable or metadata is not loaded yet.
        element.requestPictureInPicture().catch(() => {
          player.isPictureInPicture = false;
          player.triggerEvent("pictureInPictureChanged", player);
        });
        player.isPictureInPicture = true;
      }

      player.triggerEvent("pictureInPictureChanged", player);
    };
  });
}

function useVideoPlayer2PlaybackSetup() {
  useVideoPlayer2SetupStart(({ player, video }) => {
    player.play = () => {
      // play() rejects with AbortError when interrupted by pause()/a new
      // source/unmount, and NotAllowedError under autoplay policy; both are
      // expected and must not surface as unhandled rejections.
      video.play()?.catch(() => undefined);
      player.triggerEvent("play", player);
    };

    player.pause = () => {
      video.pause();
      player.triggerEvent("pause", player);
    };

    player.togglePlay = () => {
      player.element?.paused ? player.play() : player.pause();
      player.triggerEvent("playStateChanged", player);
    };

    player.forward = (duration: number) => {
      const fromTime = video.currentTime;
      video.currentTime += duration;
      player.triggerEvent("forward", player);
      
      // Use setTimeout to ensure the video time has actually changed
      setTimeout(() => {
        player.props?.onSeeking?.(player, fromTime, video.currentTime);
        player.props?.onSeeked?.(player, video.currentTime);
      }, 10);
    };

    // Debounced function for localStorage operations
    let saveCurrentTimeTimeout: NodeJS.Timeout | null = null;
    const debouncedSaveCurrentTime = () => {
      if (saveCurrentTimeTimeout) {
        clearTimeout(saveCurrentTimeTimeout);
      }
      saveCurrentTimeTimeout = setTimeout(() => {
        if (player.props?.saveCurrentTime) {
          const currentTime = video.currentTime;
          const cacheKey = currentTimeCacheKey(player);
          window.localStorage.setItem(cacheKey, currentTime.toString());
          console.log(`Saved video time: ${currentTime.toFixed(2)}s (key: ${cacheKey})`);
        }
      }, 1000); // Save every 1 second instead of 4 times per second
    };

    video.addEventListener("timeupdate", () => {
      // Keep these immediate for responsive UI
      player.triggerEvent("currentTimeChanged", player);
      player.props?.onTimeUpdate?.(player, video.currentTime, video.duration);

      // Debounce the expensive localStorage operation
      debouncedSaveCurrentTime();
    });

    player.rewind = (duration: number) => {
      const fromTime = video.currentTime;
      video.currentTime -= duration;
      player.triggerEvent("rewind", player);
      
      // Use setTimeout to ensure the video time has actually changed
      setTimeout(() => {
        player.props?.onSeeking?.(player, fromTime, video.currentTime);
        player.props?.onSeeked?.(player, video.currentTime);
      }, 10);
    };

    player.setSpeed = (speed: number) => {
      video.playbackRate = speed;
      player.triggerEvent("speedChanged", player);
    };

    player.$lazyGetter("speed", () => video?.playbackRate || 1);

    player.$lazyGetter("duration", () => video?.duration || 0);

    player.$lazyGetter("isPlaying", () => !video?.paused || false);

    player.$lazyGetter("isEnded", () => video?.ended || false);
  });
}

function useVideoPlayer2KeyboardShortcutsSetup() {
  useVideoPlayer2SetupStart(({ player }) => {
    // Space: toggle play/pause
    // F: toggle fullscreen
    // I: toggle picture in picture
    // M: toggle mute
    // Arrow right + left: Fast forward/rewind 5 seconds
    // Arrow up + down: Increase/decrease volume
    // 1: go 10% of the way forward
    // 2: go 20% of the way forward
    // 3: go 30% of the way forward
    // 4: go 40% of the way forward
    // 5: go 50% of the way forward
    // 6: go 60% of the way forward
    // 7: go 70% of the way forward
    // 8: go 80% of the way forward
    // 9: go 90% of the way forward
    // 0: go to the start
    // l/j: rewind/fast forward 10 seconds
    // k: toggle play
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === "Space" || event.code === "KeyK") {
        player.togglePlay();
      }

      if (event.code === "KeyF") {
        event.preventDefault();
        player.toggleFullscreen();
      }

      if (event.code === "KeyI") {
        event.preventDefault();
        player.togglePictureInPicture();
      }

      if (event.code === "KeyM") {
        event.preventDefault();
        player.toggleMute();
      }

      if (event.code === "ArrowRight") {
        event.preventDefault();
        player.forward(5);
      }

      if (event.code === "ArrowLeft") {
        event.preventDefault();
        player.rewind(5);
      }

      if (event.code === "ArrowUp") {
        event.preventDefault();
        player.increaseVolume(0.05);
      }

      if (event.code === "ArrowDown") {
        event.preventDefault();
        player.decreaseVolume(0.05);
      }

      if (event.code === "Digit0") {
        player.setCurrentTime(0);
      }

      if (event.code === "Digit1") {
        player.setCurrentTime(player.duration * 0.1);
      }

      if (event.code === "Digit2") {
        player.setCurrentTime(player.duration * 0.2);
      }

      if (event.code === "Digit3") {
        player.setCurrentTime(player.duration * 0.3);
      }

      if (event.code === "Digit4") {
        player.setCurrentTime(player.duration * 0.4);
      }

      if (event.code === "Digit5") {
        player.setCurrentTime(player.duration * 0.5);
      }

      if (event.code === "Digit6") {
        player.setCurrentTime(player.duration * 0.6);
      }

      if (event.code === "Digit7") {
        player.setCurrentTime(player.duration * 0.7);
      }

      if (event.code === "Digit8") {
        player.setCurrentTime(player.duration * 0.8);
      }

      if (event.code === "Digit9") {
        player.setCurrentTime(player.duration * 0.9);
      }

      if (event.code === "KeyJ") {
        player.rewind(10);
      }

      if (event.code === "KeyL") {
        player.forward(10);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  });
}

function useVideoPlayer2QualitiesSetup() {
  useVideoPlayer2SetupStart(({ player }) => {
    player.setCurrentQualityLevel = (qualityLevel: QualityLevel) => {
      player.currentQualityLevel = qualityLevel;

      if (player.hls) {
        player.hls.currentLevel = qualityLevel.id;
      }

      player.triggerEvent("qualityLevels.changed", player);
      player.triggerEvent("qualityLevels.selected", player);
    };
  });
}

export function useVideoPlayer2VideoTouchSetup() {
  useVideoPlayer2SetupStart(({ player }) => {
    let touchStartTime = 0;
    let touchStartY = 0;
    let isTouchHandled = false;

    const handleClick = (event: MouseEvent) => {
      // Prevent click on mobile devices where touch events are handled
      if (isTouchHandled) {
        event.preventDefault();
        isTouchHandled = false;
        return;
      }

      player.togglePlay();
    };

    const handleTouchStart = (event: TouchEvent) => {
      touchStartTime = Date.now();
      touchStartY = event.touches[0].clientY;
      isTouchHandled = false;
    };

    const handleTouchEnd = (event: TouchEvent) => {
      const touchEndTime = Date.now();
      const touchDuration = touchEndTime - touchStartTime;
      const touchEndY = event.changedTouches[0].clientY;
      const touchDistance = Math.abs(touchEndY - touchStartY);

      // Only handle as a tap if it's a short touch with minimal movement
      if (touchDuration < 300 && touchDistance < 10) {
        isTouchHandled = true;
        player.togglePlay();
      }
    };

    const handleDoubleClick = (event: MouseEvent) => {
      // Prevent double click on mobile
      if ("ontouchstart" in window) {
        return;
      }
      player.toggleFullscreen();
    };

    const videoElement = player.element;
    if (videoElement) {
      // Desktop events
      videoElement.addEventListener("click", handleClick);
      videoElement.addEventListener("dblclick", handleDoubleClick);

      // Mobile touch events
      videoElement.addEventListener("touchstart", handleTouchStart, {
        passive: true,
      });
      videoElement.addEventListener("touchend", handleTouchEnd, {
        passive: true,
      });
    }

    return () => {
      if (videoElement) {
        videoElement.removeEventListener("click", handleClick);
        videoElement.removeEventListener("dblclick", handleDoubleClick);
        videoElement.removeEventListener("touchstart", handleTouchStart);
        videoElement.removeEventListener("touchend", handleTouchEnd);
      }
    };
  });
}

function useVideoPlayer2LoadingSetup() {
  useVideoPlayer2SetupStart(({ player, video }) => {
    const startLoading = () => {
      player.isLoading = true;
      player.triggerEvent("loading", player);
    };

    const stopLoading = () => {
      player.isLoading = false;
      player.triggerEvent("loading", player);
    };

    video.addEventListener("waiting", startLoading);
    video.addEventListener("playing", stopLoading);
    video.addEventListener("canplay", stopLoading);

    return () => {
      video.removeEventListener("waiting", startLoading);
      video.removeEventListener("playing", stopLoading);
      video.removeEventListener("canplay", stopLoading);
    };
  });
}

function useVideoPlayer2ControlsAutoHide() {
  useVideoPlayer2SetupStart(({ player }) => {
    let timeoutId: number | null = null;

    player.setControlsVisible = (visible: boolean) => {
      player.controlsVisible = visible;
      player.triggerEvent("controlsVisible", player);
    };

    const showControls = () => {
      player.setControlsVisible(true);

      if (timeoutId) window.clearTimeout(timeoutId);

      timeoutId = window.setTimeout(() => {
        player.setControlsVisible(false);
      }, player.props.autoHideControlsAfter || 5000);
    };

    // Attach to the root container for mousemove
    const root = player.rootRef.current;

    if (root) {
      root.addEventListener("mousemove", showControls);
      // Show controls initially
      showControls();
    }

    return () => {
      if (root) root.removeEventListener("mousemove", showControls);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  });
}
